import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, CheckCircle2, Undo2, Landmark, FileDown, FileSpreadsheet, History } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, trDate, formatApiError } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AccountSelect from "@/components/AccountSelect";
import SupplierSelect from "@/components/SupplierSelect";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS_ORDER = ["portfolio", "collected", "bounced", "endorsed"];
const STATUS_TONE = {
  portfolio: "text-sky-400 border-sky-500/40 bg-sky-500/10",
  collected: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  bounced: "text-rose-400 border-rose-500/40 bg-rose-500/10",
  endorsed: "text-amber-400 border-amber-500/40 bg-amber-500/10",
};
const EMPTY = { cheque_no: "", bank: "", drawer: "", customer_id: "", supplier_id: "", due_date: "", amount: "", note: "" };
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ChequePortfolio() {
  const [direction, setDirection] = useState("in");
  const [data, setData] = useState({ rows: [], summary: {}, statuses: {} });
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const [collect, setCollect] = useState(null);
  const [collectAcc, setCollectAcc] = useState("");
  const [collectDate, setCollectDate] = useState(todayISO());
  const [busy, setBusy] = useState(null);
  const [histOf, setHistOf] = useState(null);

  const isOut = direction === "out";
  const verb = isOut ? "Öde" : "Tahsil Et";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cashbook/cheques", { params: { direction } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [direction]);
  useEffect(() => { load(); setFilter("all"); }, [load]);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);

  const statuses = data.statuses || {};
  const rows = filter === "all" ? data.rows : data.rows.filter((r) => r.status === filter);

  const openNew = () => { setForm({ ...EMPTY, due_date: todayISO() }); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ cheque_no: c.cheque_no || "", bank: c.bank || "", drawer: c.drawer || "", customer_id: c.customer_id || "",
      supplier_id: c.supplier_id || "", due_date: c.due_date || "", amount: c.amount, note: c.note || "" });
    setEditId(c.id); setOpen(true);
  };

  const save = async () => {
    if (!(parseFloat(form.amount) > 0)) { toast.error("Tutar sıfırdan büyük olmalı"); return; }
    setSaving(true);
    try {
      const payload = { ...form, direction, amount: parseFloat(form.amount) || 0,
        customer_id: form.customer_id || null, supplier_id: form.supplier_id || null, due_date: form.due_date || null };
      if (editId) { await api.put(`/cashbook/cheques/${editId}`, payload); toast.success("Çek güncellendi"); }
      else { await api.post("/cashbook/cheques", payload); toast.success("Çek eklendi"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (c, status, extra = {}) => {
    setBusy(c.id);
    try {
      await api.put(`/cashbook/cheques/${c.id}/status`, { status, ...extra });
      toast.success(`Durum: ${statuses[status] || status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const doCollect = async () => {
    if (!collectAcc) { toast.error("Hesap seçin"); return; }
    await changeStatus(collect, "collected", { account_id: collectAcc, date: collectDate });
    setCollect(null);
  };

  const remove = async () => {
    try { await api.delete(`/cashbook/cheques/${delId}`); toast.success("Çek silindi"); setDelId(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportUrl = (fmt) => `${BACKEND}/api/cashbook/cheques/export?direction=${direction}&format=${fmt}`;

  return (
    <div className="space-y-5" data-testid="cheque-portfolio">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="inline-flex rounded-xl bg-muted/50 p-1 w-fit" data-testid="cheque-direction-tabs">
          <button onClick={() => setDirection("in")} data-testid="cheque-dir-in" className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${!isOut ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Aldığımız Çekler</button>
          <button onClick={() => setDirection("out")} data-testid="cheque-dir-out" className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${isOut ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Verdiğimiz Çekler</button>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={exportUrl("pdf")} target="_blank" rel="noreferrer" data-testid="cheque-export-pdf"><Button variant="outline" className="rounded-full h-10 gap-2"><FileDown className="h-4 w-4" /> PDF</Button></a>
          <a href={exportUrl("xlsx")} target="_blank" rel="noreferrer" data-testid="cheque-export-xlsx"><Button variant="outline" className="rounded-full h-10 gap-2"><FileSpreadsheet className="h-4 w-4" /> Excel</Button></a>
          <Button onClick={openNew} data-testid="cheque-add-btn" className="rounded-full h-10 px-5 font-semibold gap-2"><Plus className="h-4 w-4" /> Çek Ekle</Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        {isOut ? "Tedarikçilere verilen çekler ayrı portföyde izlenir. Çek ödenince seçtiğiniz hesaptan otomatik gider yazılır."
               : "Müşterilerden alınan çekler ayrı portföyde izlenir. Çek tahsil edilince seçtiğiniz hesaba otomatik gelir yazılır."}
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {STATUS_ORDER.map((st) => {
          const sm = data.summary?.[st] || { count: 0, total: 0 };
          return (
            <button key={st} onClick={() => setFilter(filter === st ? "all" : st)} data-testid={`cheque-summary-${st}`}
              className={`rounded-xl border p-4 text-left transition-colors ${STATUS_TONE[st]} ${filter === st ? "ring-2 ring-offset-2 ring-offset-background ring-current" : ""}`}>
              <div className="text-xs uppercase tracking-wider opacity-80">{statuses[st] || st}</div>
              <div className="font-mono text-lg font-bold mt-1 tabular-nums">{TRY(sm.total)}</div>
              <div className="text-xs opacity-70 mt-0.5">{sm.count} çek</div>
            </button>
          );
        })}
      </div>

      {filter !== "all" && (
        <button onClick={() => setFilter("all")} data-testid="cheque-filter-clear" className="text-xs text-muted-foreground hover:text-foreground underline">
          Filtreyi temizle — tüm çekleri göster
        </button>
      )}

      <Card className="overflow-hidden" data-testid="cheque-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Vade</th><th className="px-4 py-3">Çek No</th><th className="px-4 py-3">Banka</th><th className="px-4 py-3">Keşideci / {isOut ? "Tedarikçi" : "Müşteri"}</th>
              <th className="px-4 py-3 text-right">Tutar</th><th className="px-4 py-3">Durum</th><th className="px-4 py-3 w-40"></th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground" data-testid="cheque-empty">Çek yok — "Çek Ekle" ile başlayın</td></tr>}
              {!loading && rows.map((c) => (
                <tr key={c.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`cheque-row-${c.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{c.due_date ? trDate(c.due_date) : "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{c.cheque_no || "—"}</td>
                  <td className="px-4 py-2.5">{c.bank || "—"}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{c.drawer || "—"}</div>
                    {c.party_name && <div className="text-xs text-muted-foreground">{c.party_name}</div>}
                    {c.note && <div className="text-xs text-muted-foreground italic">{c.note}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums">{TRY(c.amount)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_TONE[c.status]}`} data-testid={`cheque-status-${c.id}`}>{c.status_label}</span>
                    {c.status === "collected" && c.collected_date && <div className="text-[10px] text-muted-foreground mt-1">{trDate(c.collected_date)}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      {c.status !== "collected" ? (
                        <button onClick={() => { setCollect(c); setCollectAcc(""); setCollectDate(todayISO()); }} disabled={busy === c.id} data-testid={`cheque-collect-${c.id}`}
                          title={verb} className="h-7 w-7 rounded-md hover:bg-emerald-500/10 inline-flex items-center justify-center text-muted-foreground hover:text-emerald-400 disabled:opacity-50">
                          {busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        </button>
                      ) : (
                        <button onClick={() => changeStatus(c, "portfolio")} disabled={busy === c.id} data-testid={`cheque-revert-${c.id}`}
                          title="Geri al (portföye döndür)" className="h-7 w-7 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground hover:text-sky-400 disabled:opacity-50">
                          {busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
                        </button>
                      )}
                      {c.status === "portfolio" && <>
                        <button onClick={() => changeStatus(c, "bounced")} disabled={busy === c.id} data-testid={`cheque-bounce-${c.id}`}
                          title="Karşılıksız" className="h-7 px-2 rounded-md hover:bg-rose-500/10 inline-flex items-center justify-center text-[10px] font-semibold text-muted-foreground hover:text-rose-400 disabled:opacity-50">KARŞ.</button>
                        <button onClick={() => changeStatus(c, "endorsed")} disabled={busy === c.id} data-testid={`cheque-endorse-${c.id}`}
                          title="Ciro Edildi" className="h-7 px-2 rounded-md hover:bg-amber-500/10 inline-flex items-center justify-center text-[10px] font-semibold text-muted-foreground hover:text-amber-400 disabled:opacity-50">CİRO</button>
                      </>}
                      <button onClick={() => openEdit(c)} data-testid={`cheque-edit-${c.id}`} className="h-7 w-7 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setHistOf(c)} data-testid={`cheque-history-${c.id}`} title="Geçmiş" className="h-7 w-7 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground hover:text-sky-400"><History className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setDelId(c.id)} data-testid={`cheque-delete-${c.id}`} className="h-7 w-7 rounded-md hover:bg-destructive/10 inline-flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Çek Düzenle" : isOut ? "Yeni Verilen Çek" : "Yeni Alınan Çek"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Çek No">
              <Input value={form.cheque_no} onChange={(e) => setForm({ ...form, cheque_no: e.target.value })} data-testid="cheque-form-no" placeholder="Örn: 0012345" className="font-mono" />
            </Field>
            <Field label="Banka">
              <Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} data-testid="cheque-form-bank" placeholder="Örn: Ziraat Bankası" />
            </Field>
            <Field label="Keşideci (çeki veren)" className="sm:col-span-2">
              <Input value={form.drawer} onChange={(e) => setForm({ ...form, drawer: e.target.value })} data-testid="cheque-form-drawer" placeholder="Çeki keşide eden kişi/firma" />
            </Field>
            {isOut ? (
              <Field label="Tedarikçi (opsiyonel)" className="sm:col-span-2">
                <SupplierSelect value={form.supplier_id} onChange={(v) => setForm({ ...form, supplier_id: v })} testId="cheque-form-supplier" />
              </Field>
            ) : (
              <Field label="Müşteri (opsiyonel)" className="sm:col-span-2">
                <Select value={form.customer_id || "none"} onValueChange={(v) => setForm({ ...form, customer_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cheque-form-customer"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Yok —</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Vade Tarihi">
              <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="cheque-form-due" className="font-mono" />
            </Field>
            <Field label="Tutar (₺) *">
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cheque-form-amount" placeholder="0" className="font-mono" />
            </Field>
            <Field label="Not" className="sm:col-span-2">
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="cheque-form-note" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} disabled={saving} data-testid="cheque-form-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collect / Pay dialog */}
      <Dialog open={!!collect} onOpenChange={(o) => !o && setCollect(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Landmark className="h-5 w-5 text-emerald-400" /> {isOut ? "Çek Ödemesi" : "Çek Tahsilatı"}</DialogTitle></DialogHeader>
          {collect && <div className="space-y-4 py-2">
            <div className="rounded-lg bg-muted/50 p-3 text-sm">
              <div className="font-semibold">{collect.bank} · {collect.cheque_no || "Çek"}</div>
              <div className="text-muted-foreground">{collect.drawer || collect.party_name || "—"} · <span className="font-mono">{TRY(collect.amount)}</span></div>
            </div>
            <Field label={isOut ? "Ödeme Hesabı *" : "Tahsilat Hesabı *"}>
              <AccountSelect value={collectAcc} onChange={(v) => setCollectAcc(v)} testId="cheque-collect-account" types={["cash", "bank"]} />
            </Field>
            <Field label={isOut ? "Ödeme Tarihi" : "Tahsilat Tarihi"}>
              <Input type="date" value={collectDate} onChange={(e) => setCollectDate(e.target.value)} data-testid="cheque-collect-date" className="font-mono" />
            </Field>
            <p className="text-xs text-muted-foreground">Bu çek seçilen hesaba <b>{isOut ? "gider" : "gelir"}</b> olarak işlenecek. Cari bakiye çek eklendiğinde zaten güncellenmiştir.</p>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollect(null)}>İptal</Button>
            <Button onClick={doCollect} data-testid="cheque-collect-confirm" className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="h-4 w-4" /> {verb}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!histOf} onOpenChange={(o) => !o && setHistOf(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><History className="h-5 w-5 text-sky-400" /> Çek Geçmişi</DialogTitle></DialogHeader>
          {histOf && <div className="space-y-3 py-2" data-testid="cheque-history-list">
            <div className="text-sm text-muted-foreground">{histOf.bank} · {histOf.cheque_no || "Çek"} · <span className="font-mono">{TRY(histOf.amount)}</span></div>
            <ol className="relative border-l border-border pl-4 space-y-3">
              {(histOf.history || []).slice().reverse().map((h, i) => (
                <li key={i} className="relative" data-testid={`cheque-history-item-${i}`}>
                  <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-sky-400" />
                  <div className="text-sm font-medium">{h.label}</div>
                  <div className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString("tr-TR")}{h.by ? ` · ${h.by}` : ""}</div>
                </li>
              ))}
              {(!histOf.history || histOf.history.length === 0) && <li className="text-sm text-muted-foreground">Kayıt yok.</li>}
            </ol>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setHistOf(null)}>Kapat</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Çek silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Tahsil/ödeme yapılmışsa ilgili kasa kaydı da silinir. Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="cheque-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
